// Desglose del MRR de mensuales del mes en curso a partir de `GET /subscriptions/stripe-stats`:
//   MRR mes anterior (aprox.) − bajas que terminan este mes + nuevo MRR = MRR actual
//   MRR actual − bajas que terminan el mes que viene ≈ previsión (`monthlyBillingNextMonth`)
// Lo comparten la card "MRR" (`SubscriptionsKpis`) y la barra del mes actual (`ForecastChart`).
//
// Criterio de bajas programadas (`canceledMonthly`): solo se restan las que **terminan este mes**
// (`currentPeriodEnd` dentro del mes en curso). Esas se cobraron el mes anterior y este mes ya no
// aportan. Una baja cuyo periodo termina el mes que viene ya se ha cobrado este mes, así que sigue
// contando en el MRR de este mes (se restará el mes siguiente).
//
// Se restan desde el principio del mes para que el base no vaya bajando según se aplican: cuando
// una baja se hace efectiva sale a la vez de `monthlyBilling` y de `canceledMonthly`, y el base
// no se mueve.
//
// - base    = monthlyBilling - nuevo MRR del mes - bajas que terminan este mes de subs creadas antes del mes
// - newMrr  = nuevo MRR del mes - bajas que terminan este mes de altas del propio mes
// - current = base + newMrr
//
// La separación base / nuevo se hace cruzando por `id` con `newMonthlySubscriptionsThisMonth.items`
// (si no, un alta del mes con baja se restaría dos veces contra el base).
// Si una baja no trae `currentPeriodEnd` se asume que termina este mes (se resta).
function endsInMonth(iso, now) {
  if (!iso) return true;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return true;
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export function mrrBreakdown(stats, now = new Date()) {
  const monthlyBilling = Number(stats?.monthlyBilling) || 0;
  const newItems = stats?.newMonthlySubscriptionsThisMonth?.items || [];
  const newMrrGross = Number(stats?.newMonthlySubscriptionsThisMonth?.mrrAddedEUR) || 0;
  const newIds = new Set(newItems.map((it) => it?.id).filter(Boolean));

  let lostOld = 0;
  let lostNew = 0;
  let lostLater = 0;
  (stats?.canceledMonthly || []).forEach((c) => {
    const amount = Number(c?.cycleAmountEUR) || 0;
    if (!endsInMonth(c?.currentPeriodEnd, now)) {
      lostLater += amount;
      return;
    }
    if (c?.id && newIds.has(c.id)) lostNew += amount;
    else lostOld += amount;
  });

  const base = Math.max(monthlyBilling - newMrrGross - lostOld, 0);
  const newMrr = Math.max(newMrrGross - lostNew, 0);
  const current = base + newMrr;

  // MRR con el que cerró el mes anterior, aproximado: lo activo hoy sin las altas del mes. Incluye
  // las bajas programadas que terminan este mes (pagaron el mes pasado), pero NO las bajas que ya
  // se han hecho efectivas este mes: Stripe deja de devolverlas y el endpoint no da su importe, así
  // que este valor baja según se aplican. Con lo que hay desplegado no se puede fijar.
  const prevMonth = Math.max(monthlyBilling - newMrrGross, 0);

  // Previsión del mes siguiente: la calcula el back (mensuales activas sin baja programada).
  // Por construcción debería ser `current - lostLater`; `nextMonthDiff` avisa si no cuadra.
  const nextMonthCalc = Math.max(current - lostLater, 0);
  const nextMonth = stats?.monthlyBillingNextMonth != null ? Number(stats.monthlyBillingNextMonth) || 0 : nextMonthCalc;

  return {
    prevMonth,
    // Bajas que salen del MRR del mes anterior (subs creadas antes del mes que terminan este mes).
    // Las altas del mes con baja ya van netas en `newMrr`. prevMonth - lostPrev + newMrr = current.
    lostPrev: lostOld,
    base,
    newMrr,
    current,
    nextMonth,
    nextMonthDiff: nextMonth - nextMonthCalc,
    // Bajas que terminan este mes: ya restadas del base / nuevo MRR.
    lostMrr: lostOld + lostNew,
    // Bajas programadas que terminan el mes que viene: siguen contando este mes.
    lostNextMrr: lostLater,
  };
}
